import { InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';

import { CHANNEL } from '@boss/constants/b2b-b2c';
import { IStore, content as contentService, events as eventService } from '@boss/services';
import { Crumble, Environment } from '@boss/types/b2b-b2c';
import { getLocalizedSlugForEventPage, getLocalizedSlugsForEventPage } from '@boss/utils';

import { ComponentMapper, SEO } from '../../components';
import { IS_BUILD, PageTypes } from '../../constants';
import EventsPage from '../../containers/eventsPage/eventsPage';
import Layout from '../../layout/default';
import { eventType } from '../../utils';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapNextToContentfulLocale } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';

export const getStaticProps = async ({ preview, locale }: { preview?: boolean; locale?: string }) => {
  const stores: IStore[] = [];

  if (!locale || locale === 'default') {
    return {
      notFound: true,
    };
  }

  const contentfulLocale = mapNextToContentfulLocale(locale);

  const [navigation, overviewPage, events] = await Promise.all([
    getMappedNavigation(contentfulLocale, !!preview),
    contentService.getOverviewPage({
      locale: contentfulLocale,
      channel: CHANNEL,
      type: 'Event overview',
      batchMode: IS_BUILD,
      preview: !!preview,
    }),
    eventType === 'TRAINING'
      ? eventService.getTrainings(locale as string, IS_BUILD)
      : eventService.getWorkshops(locale as string, IS_BUILD),
  ]);

  return {
    props: {
      pageType: PageTypes.EVENT_OVERVIEW,
      events,
      stores,
      locale,
      preview: !!preview,
      pageData: { overviewPage },
      navigation,
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      initialFilters: null,
      ...(await getServerSideTranslations(locale, [
        'common',
        'stores',
        'event',
        'routes',
        'forms',
        'account',
        'workshop',
      ])),
    },
  };
};

export const Index = ({
  navigation,
  locale,
  preview,
  pageData,
  environment,
  events,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const translatedSlugs = getLocalizedSlugsForEventPage();
  const { t } = useTranslation(['event', 'stores', 'account']);
  const pageTitle = t('pageTitle', { ns: 'event' }) ?? '';
  const pageDescription = pageTitle;

  const { overviewPage } = pageData;
  const hasPrefixSection = overviewPage?.prefixSections?.length;

  const crumbles: Crumble[] = [{ href: getLocalizedSlugForEventPage(locale), label: pageTitle }];

  return (
    <SEO
      breadcrumbs={crumbles}
      description={overviewPage?.metaDescription ?? pageDescription}
      imageSrc={overviewPage?.ogImage?.image?.file?.url}
      localizedSlugs={translatedSlugs}
      navigation={navigation}
      noFollow={overviewPage?.noFollow}
      noIndex={overviewPage?.noIndex}
      title={overviewPage?.pageTitle ?? pageTitle}
    >
      <Layout
        breadcrumbClassName={hasPrefixSection ? 'mb-0 md:mb-0' : 'mb-4 md:mb-6'}
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={translatedSlugs}
        navigation={navigation}
        preview={preview}
      >
        {overviewPage?.prefixSections?.map(entry => (
          <ComponentMapper entry={entry} key={entry.id} locale={locale} />
        ))}
        <EventsPage events={events} />
        {overviewPage?.suffixSections?.map(entry => (
          <ComponentMapper entry={entry} key={entry.id} locale={locale} />
        ))}
      </Layout>
    </SEO>
  );
};

export default Index;
